import { NextPage, GetServerSideProps } from 'next'
import Link from 'next/link'
import {
  Text,
  Flex,
  Box,
  Button,
  FormatCryptoCurrency,
} from 'components/primitives'
import Layout from 'components/Layout'
import { paths } from '@reservoir0x/reservoir-sdk'
import { useContext, useEffect, useState } from 'react'
import { Footer } from 'components/home/Footer'
import { useMarketplaceChain } from 'hooks'
import supportedChains, { DefaultChain } from 'utils/chains'
import { Head } from 'components/Head'
import { ChainContext } from 'context/ChainContextProvider'

import Img from 'components/primitives/Img'
import SafeImg from 'components/primitives/SafeImg'
import useTopSellingCollections from 'hooks/useTopSellingCollections'
import ReactMarkdown from 'react-markdown'
import { basicFetcher as fetcher } from 'utils/fetcher'
import { ssrApiBase } from 'utils/ssrApiBase'
import { useTheme } from 'next-themes'
import ChainToggle from 'components/common/ChainToggle'
import optimizeImage from 'utils/optimizeImage'
import { MarkdownLink } from 'components/primitives/MarkdownLink'
import { useRouter } from 'next/router'

import { getSiteCollections } from 'utils/siteConfig.server'


const Home: NextPage<any> = ({ ssr }) => {
  const router = useRouter()
  const marketplaceChain = useMarketplaceChain()

  // not sure if there is a better way to fix this
  const { theme: nextTheme } = useTheme()
  const [theme, setTheme] = useState<string | null>(null)
  useEffect(() => {
    if (nextTheme) {
      setTheme(nextTheme)
    }
  }, [nextTheme])

  const { chain } = useContext(ChainContext)

  const { data: topSellingCollectionsData } = useTopSellingCollections(
    {
      period: '24h',
      includeRecentSales: true,
      limit: 9,
      fillType: 'sale',
    },
    {
      revalidateOnMount: true,
      refreshInterval: 300000,
      fallbackData: ssr.topSellingCollections[marketplaceChain.id]?.collections
        ? ssr.topSellingCollections[marketplaceChain.id]
        : null,
    },
    chain?.id
  )

  const brandedCollections: any[] = ssr.brandedCollections ?? []

  // Artwork overrides must also apply to client-side refetches, which never
  // pass through getServerSideProps.
  const applyClientArtwork = (c: any) => {
    if (!c) return c
    const art = (ssr.artwork ?? {})[String(c.id || '').toLowerCase()]
    if (!art) return c
    return {
      ...c,
      image: c.image || art.image,
      banner: c.banner || art.banner,
    }
  }

  // The hero must never render empty: featured -> top-selling -> first
  // branded collection with artwork (preferring the current chain).
  const brandedWithArtwork = brandedCollections.filter(
    (c) => c && (c.banner || c.image)
  )
  const topCollection =
    applyClientArtwork(ssr.featuredCollection) ??
    applyClientArtwork(topSellingCollectionsData?.collections?.[0]) ??
    brandedWithArtwork.find(
      (c) => (c.chain ?? c.brandChain) === marketplaceChain?.routePrefix
    ) ??
    brandedWithArtwork[0]
  // A branded fallback may belong to the other chain; link to its own.
  const topCollectionChain =
    (topCollection as any)?.chain ??
    (topCollection as any)?.brandChain ??
    chain?.routePrefix

  const useBranded = brandedCollections.length > 0
  const gridCollections: any[] = useBranded
    ? brandedCollections
    : topSellingCollectionsData?.collections?.slice(1, 9) ?? []

  return (
    <Layout>
      <Head />
      <Box
        css={{
          p: 24,
          height: '100%',
          '@bp800': {
            px: '$5',
          },
          '@xl': {
            px: '$6',
          },
        }}
      >
        <Link href={`/${topCollectionChain}/collection/${topCollection?.id}`}>
          <Flex>
            <Flex
              css={{
                '&:hover button': {
                  background: theme == 'light' ? '$primary11' : '$gray2',
                },
                minHeight: 540,
                flex: 1,
                overflow: 'hidden',
                position: 'relative',
                gap: '$5',
                p: '$4',
                display: 'none',
                '@md': {
                  p: '$5',
                  gap: '$4',
                  flexDirection: 'column',
                  display: 'flex',
                },
                '@lg': {
                  flexDirection: 'row',
                  p: '$5',
                  gap: '$5',
                  mt: '$4',
                },
                '@xl': {
                  p: '$6',
                  gap: '$6',
                },

                mb: '$6',
                maxWidth: 1820,
                mx: 'auto',
                borderRadius: 16,
                backgroundSize: 'cover',
                border: `1px solid $gray5`,
                backgroundImage:
                  theme === 'light'
                    ? `url(${optimizeImage(topCollection?.banner, 1820)})`
                    : '$gray3',
                backgroundColor: '$gray5',
              }}
            >
              <Box
                css={{
                  position: 'absolute',
                  top: 0,
                  display: theme === 'light' ? 'block' : 'none',
                  zIndex: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backdropFilter: 'blur(20px)',
                  background: 'rgba(255, 255, 255, 0.9)',
                }}
              />

              {topSellingCollectionsData && (
                <>
                  <Box
                    css={{
                      flex: 2,
                      position: 'relative',
                      zIndex: 5,
                      '@xl': {
                        flex: 3,
                      },
                    }}
                  >
                    <SafeImg
                      src={optimizeImage(
                        topCollection?.banner ?? topCollection?.image,
                        1820
                      )}
                      alt={topCollection?.name}
                      css={{
                        width: '100%',
                        borderRadius: 8,
                        height: 320,
                        '@lg': {
                          height: 540,
                        },
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      css={{
                        position: 'absolute',
                        left: '$4',
                        '@lg': {
                          top: '$4',
                        },
                        bottom: '$4',
                      }}
                    >
                      <Img
                        alt="collection image"
                        width={100}
                        height={100}
                        style={{
                          display: 'block',
                          borderRadius: 8,
                          border: '2px solid rgba(255,255,255,0.6)',
                        }}
                        src={optimizeImage(topCollection?.image, 200) as string}
                      />
                    </Box>
                  </Box>
                  <Box css={{ flex: 2, zIndex: 4 }}>
                    <Flex direction="column" css={{ height: '100%' }}>
                      <Box css={{ flex: 1 }}>
                        <Text style="h3" css={{ mt: '$3', mb: '$2' }} as="h3">
                          {topCollection?.name}
                        </Text>

                        <Box
                          css={{
                            maxWidth: 720,
                            lineHeight: 1.5,
                            fontSize: 16,
                            fontWeight: 400,
                            display: '-webkit-box',
                            color: '$gray12',
                            fontFamily: '$body',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <ReactMarkdown
                            children={topCollection?.description || ''}
                            components={{
                              a: MarkdownLink,
                              p: Text as any,
                            }}
                          />
                        </Box>

                        <Flex css={{ mt: '$4' }}>
                          <Box css={{ mr: '$5' }}>
                            <Text style="subtitle2" color="subtle">
                              FLOOR
                            </Text>
                            <Box css={{ mt: 2 }}>
                              <FormatCryptoCurrency
                                amount={
                                  topCollection?.floorAsk?.price?.amount
                                    ?.native ?? 0
                                }
                                textStyle={'h4'}
                                logoHeight={20}
                                address={
                                  topCollection?.floorAsk?.price?.currency
                                    ?.contract
                                }
                              />
                            </Box>
                          </Box>

                          <Box css={{ mr: '$4' }}>
                            <Text style="subtitle2" color="subtle">
                              24H SALES
                            </Text>
                            <Text style="h4" as="h4" css={{ mt: 2 }}>
                              {topCollection?.count?.toLocaleString()}
                            </Text>
                          </Box>
                        </Flex>
                        <Box
                          css={{
                            display: 'none',
                            '@lg': {
                              display: 'block',
                            },
                          }}
                        >
                          <Text
                            style="subtitle2"
                            color="subtle"
                            as="p"
                            css={{ mt: '$4' }}
                          >
                            RECENT SALES
                          </Text>
                          <Flex
                            css={{
                              mt: '$2',
                              gap: '$3',
                            }}
                          >
                            {topCollection?.recentSales
                              ?.slice(0, 4)
                              ?.map((sale: any, i: number) => (
                                <Box
                                  css={{
                                    aspectRatio: '1/1',
                                    maxWidth: 120,
                                  }}
                                  key={sale.token.id + sale.contract + i}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    router.push(
                                      `/${chain.routePrefix}/asset/${topCollection.primaryContract}:${sale.token.id}`
                                    )
                                  }}
                                >
                                  <SafeImg
                                    iconSize="1x"
                                    css={{ borderRadius: 4, width: '100%' }}
                                    alt={sale?.token?.name}
                                    src={optimizeImage(
                                      sale?.token?.image ||
                                        topCollection?.image,
                                      250
                                    )}
                                  />
                                  <Box css={{ mt: '$1' }}>
                                    <FormatCryptoCurrency
                                      amount={sale.price.amount.decimal ?? 0}
                                      textStyle={'h6'}
                                      logoHeight={16}
                                      address={sale.price.currency?.contract}
                                    />
                                  </Box>
                                </Box>
                              ))}
                            <Box css={{ flex: 1 }} />
                            <Box css={{ flex: 1 }} />
                          </Flex>
                        </Box>
                      </Box>
                      <Flex css={{ gap: '$4', mt: '$5' }}>
                        {theme == 'light' ? (
                          <Button as="button" color="primary" size="large">
                            Explore Collection
                          </Button>
                        ) : (
                          <Button as="button" color="gray4" size="large">
                            Explore Collection
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </Link>
        <Flex
          id="collections"
          justify="between"
          align="center"
          css={{ flexWrap: 'wrap', mb: '$4', gap: '$3', scrollMarginTop: 100 }}
        >
          <Text style="h4" as="h4">
            {useBranded ? 'Branded Collections' : 'Trending Collections'}
          </Text>
          {!useBranded && <ChainToggle />}
        </Flex>
        <Box
          css={{
            pt: '$2',
            mb: '$4',
            display: 'grid',
            gap: '$4',
            gridTemplateColumns: 'repeat(1, 1fr)',
            '@sm': {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },

            '@lg': {
              gridTemplateColumns: 'repeat(4, 1fr)',
            },
          }}
        >
          {gridCollections.map((collection) => {
                return (
                  <Link
                    key={collection.id}
                    href={`/${
                      collection.brandChain || marketplaceChain.routePrefix
                    }/collection/${collection.id}`}
                    style={{ display: 'grid' }}
                  >
                    <Flex
                      direction="column"
                      css={{
                        flex: 1,
                        width: '100%',
                        borderRadius: 12,
                        cursor: 'pointer',
                        height: '100%',
                        background: '$neutralBgSubtle',
                        $$shadowColor: '$colors$panelShadow',
                        boxShadow:
                          '0 1px 2px 0 rgba(0,0,0,0.05), 0 12px 32px -12px $$shadowColor, inset 0 1px 0 0 rgba(255,255,255,0.5)',
                        border: '1px solid $panelBorder',
                        overflow: 'hidden',
                        position: 'relative',
                        p: '$4',
                        transition:
                          'transform 250ms ease, box-shadow 250ms ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow:
                            '0 2px 4px 0 rgba(0,0,0,0.06), 0 24px 48px -16px $$shadowColor, inset 0 1px 0 0 rgba(255,255,255,0.5)',
                        },
                        '&:hover > div > div> img:nth-child(1)': {
                          transform: 'scale(1.075)',
                        },
                      }}
                    >
                      <Flex
                        direction="column"
                        css={{
                          zIndex: 2,
                          position: 'relative',
                          flex: 1,
                          width: '100%',
                        }}
                      >
                        <Box
                          css={{
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 8,
                          }}
                        >
                          {collection?.banner?.length ||
                          collection.recentSales?.[0]?.token?.image?.length ? (
                            <>
                              <SafeImg
                                loading="lazy"
                                alt={collection?.name}
                                src={optimizeImage(
                                  collection?.banner ??
                                    collection.recentSales?.[0]?.token?.image,
                                  800
                                )}
                                css={{
                                  transition: 'transform 300ms ease-in-out',
                                  width: '100%',
                                  borderRadius: 8,
                                  height: 250,
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                              <Box
                                css={{
                                  position: 'absolute',
                                  inset: 0,
                                  pointerEvents: 'none',
                                  borderRadius: 8,
                                  background:
                                    'linear-gradient(180deg, rgba(255,255,255,0.20), rgba(255,255,255,0) 45%, rgba(0,0,0,0.08))',
                                }}
                              />
                            </>
                          ) : collection?.image ? (
                            // No banner art: fill with the collection's own
                            // image, blown up + blurred, under a glass sheen.
                            <Box
                              css={{
                                position: 'relative',
                                width: '100%',
                                height: 250,
                                borderRadius: 8,
                                overflow: 'hidden',
                                background: '$gray3',
                              }}
                            >
                              <SafeImg
                                loading="lazy"
                                alt=""
                                src={optimizeImage(collection.image, 800)}
                                css={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  filter:
                                    'blur(28px) brightness(0.88) saturate(1.25)',
                                  transform: 'scale(1.25)',
                                  transition: 'transform 300ms ease-in-out',
                                }}
                              />
                              <Box
                                css={{
                                  position: 'absolute',
                                  inset: 0,
                                  pointerEvents: 'none',
                                  background:
                                    'linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0) 45%, rgba(0,0,0,0.10))',
                                }}
                              />
                            </Box>
                          ) : (
                            <Box
                              css={{
                                width: '100%',
                                borderRadius: 8,
                                height: 250,
                                background: '$gray3',
                              }}
                            />
                          )}
                          <Img
                            src={
                              optimizeImage(collection?.image, 72 * 2) as string
                            }
                            alt={collection?.name as string}
                            width={72}
                            height={72}
                            css={{
                              width: 72,
                              height: 72,
                              border: '2px solid rgba(255,255,255,0.6)',
                              position: 'absolute',
                              bottom: '$3',
                              left: '$3',
                              borderRadius: 8,
                            }}
                          />
                        </Box>
                        <Flex
                          css={{ my: '$4', mb: '$2' }}
                          justify="between"
                          align="center"
                        >
                          <Text style="h5" as="h5" ellipsify css={{ flex: 1 }}>
                            {collection?.name}
                          </Text>
                        </Flex>

                        <Box
                          css={{
                            maxWidth: 720,
                            lineHeight: 1.5,
                            fontSize: 16,
                            flex: 1,
                            fontWeight: 400,
                            display: '-webkit-box',
                            color: '$gray12',
                            fontFamily: '$body',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            '& a': {
                              fontWeight: 500,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          <ReactMarkdown
                            children={collection?.description || ''}
                            components={{
                              a: MarkdownLink,
                              p: Text as any,
                            }}
                          />
                        </Box>

                        <Flex css={{ mt: '$4' }}>
                          <Box css={{ mr: '$5' }}>
                            <Text
                              style="subtitle2"
                              color="subtle"
                              as="p"
                              css={{ mb: 2 }}
                            >
                              FLOOR
                            </Text>
                            <FormatCryptoCurrency
                              amount={
                                collection?.floorAsk?.price?.amount?.native ?? 0
                              }
                              textStyle={'h6'}
                              logoHeight={12}
                              address={
                                collection?.floorAsk?.price?.currency?.contract
                              }
                            />
                          </Box>

                          <Box css={{ mr: '$4' }}>
                            <Text style="subtitle2" color="subtle" as="p">
                              24H SALES
                            </Text>
                            <Text style="h6" as="h4" css={{ mt: 2 }}>
                              {collection.count?.toLocaleString()}
                            </Text>
                          </Box>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Link>
                )
              })}
        </Box>
        {!useBranded && (
          <Box css={{ my: '$5' }}>
            <Link href={`/${marketplaceChain.routePrefix}/collections/trending`}>
              <Button>See More</Button>
            </Link>
          </Box>
        )}
      </Box>

      <Footer />
    </Layout>
  )
}

type TopSellingCollectionsSchema =
  paths['/collections/top-selling/v1']['get']['responses']['200']['schema']

type ChainTopSellingCollections = Record<string, TopSellingCollectionsSchema>

export const getServerSideProps: GetServerSideProps<{
  ssr: {
    topSellingCollections: ChainTopSellingCollections
    featuredCollection: any
    brandedCollections: any[]
    artwork: Record<string, { image?: string; banner?: string }>
  }
}> = async ({ params, res }) => {
  const chainPrefix = params?.chain || ''
  const chain =
    supportedChains.find((chain) => chain.routePrefix === chainPrefix) ||
    DefaultChain

  const topSellingCollections: ChainTopSellingCollections = {}
  try {
    const response = await fetcher(
      `${ssrApiBase(
        chain.routePrefix
      )}/collections/top-selling/v2?period=72h&includeRecentSales=true&limit=9&fillType=Sale`
    )

    topSellingCollections[chain.id] = response.data

    res.setHeader(
      'Cache-Control',
      'public, s-maxage=120, stale-while-revalidate=180'
    )
  } catch (e) {}

  // Pinned hero collection + curated brand showcase come from the site
  // config (editable by the admin wallet at /admin), not hardcoded.
  const siteCollections = getSiteCollections()
  // Artwork imported from OpenSea fills image/banner gaps Alchemy lacks.
  const applyArtwork = (c: any) => {
    if (!c) return c
    const art = siteCollections.artwork?.[String(c.id || '').toLowerCase()]
    if (art?.image && !c.image) c.image = art.image
    if (art?.banner && !c.banner) c.banner = art.banner
    return c
  }
  let featuredCollection: any = null
  const featuredAddress = siteCollections.featured[chain.routePrefix]
  if (featuredAddress) {
    try {
      const featuredResponse = await fetcher(
        `${ssrApiBase(
          chain.routePrefix
        )}/collections/v7?contract=${featuredAddress}&limit=1`
      )
      featuredCollection = applyArtwork(
        featuredResponse.data?.collections?.[0] ?? null
      )
    } catch (e) {}
    // Reservoir is down: for our own Singles/Editions contracts, build the
    // hero from on-chain token #1 metadata so it never renders blank.
    if (!featuredCollection) {
      try {
        const { createPublicClient, http } = await import('viem')
        const { polygon } = await import('viem/chains')
        const { SINGLES_ABI } = await import('utils/editions')
        const client = createPublicClient({
          chain: polygon,
          transport: http('https://polygon-bor-rpc.publicnode.com'),
        })
        const uri = (await client.readContract({
          address: featuredAddress as `0x${string}`,
          abi: SINGLES_ABI,
          functionName: 'tokenURI',
          args: [1n],
        })) as string
        const httpUri = uri.startsWith('ipfs://')
          ? `https://gateway.pinata.cloud/ipfs/${uri.slice(7)}`
          : uri
        const meta = await (await fetch(httpUri)).json()
        const img = meta.image?.startsWith('ipfs://')
          ? `https://gateway.pinata.cloud/ipfs/${meta.image.slice(7)}`
          : meta.image
        if (img) {
          featuredCollection = {
            id: featuredAddress,
            name: 'Audi0 Singles',
            description: meta.description || '',
            image: img,
            banner: img,
            chain: 'polygon',
          }
        }
      } catch (e) {}
    }
  }

  // One unified branded set: each entry carries its own chain toggle, so a
  // collection's data is fetched from its chain's API and its card links to
  // that chain's collection page. The same grid renders on every chain's
  // landing page, in the configured order.
  let brandedCollections: any[] = []
  const brandedEntries = (siteCollections.branded || []).filter(
    (e) =>
      e &&
      typeof e.address === 'string' &&
      supportedChains.some((c) => c.routePrefix === e.chain)
  )
  if (brandedEntries.length) {
    const byChain: Record<string, string[]> = {}
    for (const e of brandedEntries) {
      if (!byChain[e.chain]) byChain[e.chain] = []
      if (!byChain[e.chain].includes(e.address)) byChain[e.chain].push(e.address)
    }
    const byId: Record<string, any> = {}
    await Promise.all(
      Object.entries(byChain).map(async ([entryChain, addresses]) => {
        try {
          const brandedResponse = await fetcher(
            `${ssrApiBase(entryChain)}/collections/v7?contract=${addresses.join(
              ','
            )}&limit=50`
          )
          for (const c of brandedResponse.data?.collections || []) {
            if (c?.id)
              byId[`${entryChain}:${String(c.id).toLowerCase()}`] = c
          }
        } catch (e) {}
      })
    )
    brandedCollections = brandedEntries
      .map((e) => {
        const c = byId[`${e.chain}:${e.address.toLowerCase()}`]
        if (!c) return null
        c.brandChain = e.chain
        return applyArtwork(c)
      })
      .filter(Boolean)
  }

  return {
    props: {
      ssr: {
        topSellingCollections,
        featuredCollection,
        brandedCollections,
        artwork: siteCollections.artwork ?? {},
      },
    },
  }
}

export default Home
